import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useModal } from 'hooks/useModal';
import { Loop } from '@mui/icons-material';
import { grey } from '@mui/material/colors';
import { ModalsKeys, Nullable } from 'global';
import { useInvoice } from 'modules/invoice';
import { useFormContext } from 'react-hook-form';
import { useMutation, useQuery } from '@apollo/client';
import { Button, Skeleton, Stack } from '@mui/material';
import { EquipmentType } from '@trnsact/trnsact-shared-types';
import { Equipment, useEquipmentActions } from 'modules/equipment';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';
import {
  Quote,
  useQuote,
  quoteAPI,
  quoteMapper,
  QuoteLineItemType,
  DealerProfitability,
  InvoiceDetailsListing,
  NetFinanceAmountListing,
  QuoteChargeListingEntity,
} from 'modules/quote';

import { hasTradeInData } from '../../../lib';
import { TradeInSection } from './tradeInTable';
import { DealBuilderFormValues } from '../../../types';
import { useDealBuilderContext } from '../../../context';
import { CustomerDepositForm } from './CustomerDepositForm';
import { DealBuilderIds, StepComponentProps } from '../../../types';
import { dealBuilderApi, UpdateDealBuilderQuoteResponse, UpdateDealBuilderQuoteVariables } from '../../../api';

export const PricingStep = ({ navigationNode }: StepComponentProps) => {
  const [chargeData, setChargeData] = useState<Nullable<QuoteChargeListingEntity[]>>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const { handleOpen, handleClose } = useModal(ModalsKeys.Confirmation);
  const {
    getValues,
    setValue,
    formState: { dirtyFields },
  } = useFormContext<DealBuilderFormValues>();

  const {
    activeEntity,
    setActiveEntity,
    pricing: { priceCalc },
    sourceData: { voId, dynamicsVOId },
    equipment: { selectedEquipmentItem },
  } = useDealBuilderContext();

  const { handleFetchQuoteById } = useQuote();
  const { onUpdateEquipment, onUpsertEquipmentRecord } = useEquipmentActions();
  const { handleUpdateInvoice, handleApplyFeesAndTaxes, isApplyingFeesAndTaxes } = useInvoice();

  const [isRefreshingQuote, setIsRefreshingQuote] = useState<boolean>(false);

  const hasAppliedFeesAndTaxes = (activeEntity?.quote?.feesAndTaxesApplyCount ?? 0) > 0;

  // Do not reinitialize the listing when quote data changes
  const stableQuote = useMemo(() => (activeEntity?.quote ?? null) as unknown as Nullable<Quote>, [activeEntity?.quote]);

  const {
    data: equipmentQueryData,
    loading: equipmentLoading,
    refetch: refetchEquipment,
  } = useQuery<Record<'equipment', Equipment>, Record<'id', string>>(dealBuilderApi.getEquipmentDetailsById, {
    skip: !selectedEquipmentItem?.equipment.id,
    variables: {
      id: selectedEquipmentItem?.equipment.id!,
    },
  });

  const [updateDealBuilderQuoteMutation, { error }] = useMutation<
    UpdateDealBuilderQuoteResponse,
    UpdateDealBuilderQuoteVariables
  >(dealBuilderApi.updateDealBuilderQuote, {
    awaitRefetchQueries: true,
    refetchQueries: [quoteAPI.getQuoteCalcById],
  });

  const updateQuote = async (quoteId: string, input: Record<string, unknown>) => {
    const result = await updateDealBuilderQuoteMutation({
      variables: {
        input,
        quoteId,
      },
    });

    const updatedQuote = result.data?.dealBuilder?.updateDealBuilderQuote?.entity;

    if (updatedQuote && activeEntity) {
      setActiveEntity({
        ...activeEntity,
        quote: updatedQuote,
      });
    }
  };

  const persistTradeInRows = async () => {
    const tradeInRows = getValues('pricing.tradeIn');

    const persistedRows = await Promise.all(
      tradeInRows.map(async (row, rowIndex) => {
        if (!hasTradeInData(row)) return null;

        if (row.equipmentId) return row;

        const equipmentId = await onUpsertEquipmentRecord({
          quantity: 1,
          make: row.make,
          model: row.model,
          year: row.year,
          mileage: row.mileage,
          type: EquipmentType.TradeIn,
          newUsed: row.newUsed ?? null,
          tradeInValue: row.tradeInValue,
          amountOwedOnTradeIn: row.amountOwed,
          equipmentId: undefined,
          serialNumberVin: row.serialNumberVin ?? '',
        });

        setValue(`pricing.tradeIn.${rowIndex}.equipmentId`, equipmentId);

        return { ...row, equipmentId };
      })
    );

    return persistedRows
      .filter(
        (
          row
        ): row is DealBuilderFormValues['pricing']['tradeIn'][number] & {
          equipmentId: string;
        } => Boolean(row?.equipmentId)
      )
      .map(row => ({
        equipmentId: row.equipmentId,
        vin: row.serialNumberVin || undefined,
        tradeInDetails: {
          tradeValue: row.tradeInValue ?? 0,
          tradeAmountOwed: row.amountOwed ?? 0,
          actualCashValue: row.actualCashValue ?? 0,
          overUnderAllowance: row.overUnderAllowance ?? 0,
          tradePayoffLienholder: row.payoffLienholder || undefined,
          tradePayoffAccountNumber: row.payoffAccountNumber || undefined,
          tradePayoffGoodThroughDate: row.payoffGoodThroughDate || undefined,
        },
      }));
  };

  const handleSaveStepChanges = async ({ quoteId }: DealBuilderIds) => {
    if (!Array.isArray(chargeData)) return;

    try {
      setLoading(true);

      const customerDeposit = getValues('pricing.customerDeposit');
      const isCustomerDepositDirty = Boolean(dirtyFields.pricing?.customerDeposit);

      const quoteDTO = quoteMapper.toQuoteVariables(chargeData);
      const invoiceDTO = quoteMapper.toInvoiceDTO(quoteDTO);
      const equipmentTradeIns = await persistTradeInRows();

      const { equipmentTradeIns: _, ...quoteInput } = quoteDTO;

      const listOfEquipmentPromises = chargeData
        .filter(({ type }) => type === QuoteLineItemType.Equipment)
        .map(({ equipment }) => ({
          ...quoteMapper.toEquipmentSyncDTO(equipment!),
          vendorOpportunityId: voId,
          equipmentId: equipment!.equipmentId,
          dynamicsVendorOpportunityId: dynamicsVOId,
        }))
        .map(dto => onUpdateEquipment(dto, { withNotification: false }));

      await Promise.all([
        ...listOfEquipmentPromises,
        updateQuote(quoteId, {
          ...quoteInput,
          vendorOpportunityId: voId,
          attributes: quoteMapper.toQuoteAttributesWithAppliedOptions(stableQuote, equipmentList),
          ...(isCustomerDepositDirty ? { deposit: customerDeposit } : {}),
          equipmentTradeIns,
        }),
        handleUpdateInvoice({ VOId: voId, entityId: dynamicsVOId, fieldsObj: invoiceDTO }),
      ]);
    } finally {
      setLoading(false);
    }
  };

  const equipmentList = useMemo<Equipment[]>(() => {
    if (!equipmentQueryData?.equipment) return [];
    return [{ ...equipmentQueryData.equipment, vendorOpportunityId: voId }];
  }, [equipmentQueryData?.equipment, voId]);

  // Persist added/removed equipment options as soon as they change so they survive reload and step switches
  const isPersistingOptionsRef = useRef<boolean>(false);

  const handleRowsChange = useCallback(
    async (rows: QuoteChargeListingEntity[]) => {
      const quoteId = activeEntity?.quote?.quoteId;

      if (!quoteId || isPersistingOptionsRef.current) return;
      if (!quoteMapper.hasEquipmentOptionChanges(stableQuote, equipmentList, rows)) return;

      isPersistingOptionsRef.current = true;

      try {
        const { fees } = quoteMapper.toQuoteVariables(rows);

        await updateQuote(quoteId, {
          fees,
          vendorOpportunityId: voId,
          attributes: quoteMapper.toQuoteAttributesWithAppliedOptions(stableQuote, equipmentList),
        });
      } finally {
        isPersistingOptionsRef.current = false;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeEntity?.quote?.quoteId, stableQuote, equipmentList, voId]
  );

  const handleReapplyTaxesAndFees = () => {
    handleOpen({
      title: 'Confirmation',
      message:
        'Warning - all existing taxes, fees, charges, and discounts on this invoice and associated equipment will be removed and replaced. Continue?',
      onConfirm: async () => {
        handleClose();
        const quoteId = activeEntity?.quote?.quoteId;
        if (!quoteId) return;

        setIsRefreshingQuote(true);
        await handleApplyFeesAndTaxes(voId, quoteId, () => ({
          awaitRefetchQueries: true,
          refetchQueries: [quoteAPI.getQuoteCalcById],
        }));

        const [refreshedQuote] = await Promise.all([handleFetchQuoteById(quoteId), refetchEquipment()]);
        if (refreshedQuote && activeEntity) {
          setActiveEntity({
            ...activeEntity,
            quote: refreshedQuote as unknown as typeof activeEntity.quote,
          });
        }

        setIsRefreshingQuote(false);
      },
    });
  };

  return (
    <CardContainer
      title="Pricing"
      subTitle="Edit pricing line items, add charges, and review pricing before building a payment structure"
    >
      <RenderWithFallback
        isLoading={equipmentLoading || isRefreshingQuote}
        fallBack={<Skeleton variant="rounded" width="100%" height={400} />}
      >
        <InvoiceDetailsListing
          VOId={voId}
          quote={stableQuote}
          dynamicsVOId={dynamicsVOId}
          onSubscribe={setChargeData}
          onRowsChange={handleRowsChange}
          equipmentList={equipmentList}
          onEquipmentUpdated={refetchEquipment}
          actionPanelBottom={() => (
            <Stack direction="row" gap="1rem" sx={{ paddingBottom: '0.5rem', borderBottom: `1px solid ${grey[300]}` }}>
              <Button startIcon={<Loop />} loading={isApplyingFeesAndTaxes} onClick={handleReapplyTaxesAndFees}>
                {hasAppliedFeesAndTaxes ? 'Reapply Taxes and Fees' : 'Apply Taxes and Fees'}
              </Button>
            </Stack>
          )}
        />

        <TradeInSection />

        <CustomerDepositForm />

        <NetFinanceAmountListing netFinanceAmount={priceCalc?.netFinanceAmount ?? null} />

        <DealerProfitability />
      </RenderWithFallback>

      {navigationNode({
        error,
        isLoading: loading,
        fn: handleSaveStepChanges,
        isDisabled: !Array.isArray(chargeData),
      })}
    </CardContainer>
  );
};
